/**
 * layout - the batch table's binding of the shared detail-page surface.
 *
 * A table has three panes over its canvas, all on the RIGHT:
 *
 *   detail      the selected CELL - its answer, sources, comments   (right)
 *   bulkReview  pick documents to send to a repaper review          (right)
 *   activity    the table's audit log                               (right)
 *
 * The centre is not a panel. It is the CANVAS - the grid itself, always
 * present, never tiled, with the panes floating over its right edge.
 *
 * NO TABS. A table page has one thing to show and it is the grid, so `tabs` is
 * left off entirely (see `defineDetailSurface`: an undeclared vocabulary
 * accepts everything, which is what a surface with no tab bar wants) and the
 * page renders no `DetailTabBar`. The rail lives in the table's own toolbar
 * instead, which is the row above the surface.
 *
 * NO READER. Opening a document from a row is a full-attention act at 85% of
 * the window with a redline and a page navigator - it stays in the global side
 * panel. Declaring a reader pane nothing fills is exactly the empty-pane
 * mistake the shared README warns about.
 *
 * WHY ENTITY SCOPE. A compliance table worked cell-by-cell and an extraction
 * table read as a whole want different panes open, and one shared key would
 * make every table inherit the arrangement of whichever was opened last.
 *
 * WHY THE CELL DETAIL IS A PANE AGAIN. It was one briefly and broke: the pane
 * took its width from `dockWidth`, which reconciles the panes VISIBLE in a
 * dock, while the only thing that opened it was a `selectedCell` state field.
 * Nothing set `visible`, so the dock held no panes, the width came back 0, and
 * clicking a cell mounted a panel exactly zero pixels wide. The fix is not a
 * missing line - it is that there is now ONE answer to "is the cell detail
 * open?": `useDetailSurface`'s selection, which opens and raises `detailPane`
 * as part of selecting, and closes it as part of clearing.
 */

import {
  DockSide,
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';
import { isAiUsageEnabled } from '../../../config/featureFlags';

export type { DockSide };

export type TablePanelId =
  | 'detail'
  | 'bulkReview'
  | 'access'
  | 'activity'
  | 'aiUsage';

export type TablePanelSlot = GenericSlot<TablePanelId>;
export type TablePanelLayout = GenericLayout<TablePanelId>;

/* AI usage is dropped from the surface where the feature is not released —
   no slot, no rail pill, no persisted visibility, so a pane can never open
   onto it. See `config/featureFlags.isAiUsageEnabled`. */
export const TABLE_PANEL_IDS: TablePanelId[] = [
  'detail',
  'bulkReview',
  'access',
  'activity',
  ...(isAiUsageEnabled() ? (['aiUsage'] as TablePanelId[]) : []),
];

const TABLE_PANES: Record<TablePanelId, DetailPaneConfig> = {
  /*
    The DETAIL container: the one cell that is selected. It leads the right
    stack because it is the pane the page is worked through - a cell only
    means anything beside its row, which is why this is a dock and not the
    full-height overlay it used to be. Wide bounds: the answer, the source
    excerpt and a comment thread all live in here.

    NO RAIL TOGGLE. It opens because a cell was clicked; a pill that does
    nothing until you touch the grid reads as a broken control.
  */
  detail: {
    label: 'Cell',
    bounds: { min: 360, max: 900 },
    dock: 'right', index: 0, width: 480, visible: false,
  },
  /*
    Bulk repaper review - compliance tables only, and the page passes it only
    when `mode === 'compliance'`, so on every other table the pane simply never
    draws. Opened from the toolbar's "Review for Repaper", so no rail toggle
    either.
  */
  bulkReview: {
    label: 'Repaper Review',
    bounds: { min: 320, max: 760 },
    dock: 'right', index: 1, width: 440, visible: false,
  },
  /*
    ACCESS. Who a table is shared with was a decorative avatar row in the
    header (not pressable) plus a "Manage Access" menu item that opened the
    write panel - so the only way to READ the list was to open the editor for
    it. A due-diligence table is worked by a team; the roster is read far more
    often than it is changed.
  */
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 2, width: 300, visible: false, rail: true,
  },
  // Activity and Access are the panes someone opens because they want them,
  // so they are the panes with rail toggles.
  activity: {
    label: 'Activity',
    bounds: { min: 260, max: 720 },
    dock: 'right', index: 3, width: 340, visible: false, rail: true,
  },
  /*
    AI USAGE. A table is the most expensive thing in the app to run - a column
    times a hundred documents is a hundred model calls - and until now the
    only evidence of that was the bill at the end of the month. This pane
    answers "what did this table cost" beside the table itself.

    A PANE, NOT A TAB, and that is not an accident of this page: a table has
    no tab bar at all (the grid IS the canvas), a review session's canvas is
    its document and a draft's is its paper. Only matters has content tabs. So
    the one placement that is the same on all four surfaces is a docked pane —
    which is also the right category, since usage is reference read AGAINST
    the work, exactly like Access and Activity.

    Floor of 300: the run rows carry a face, a relative time and a cost on one
    baseline, and below that they wrap into three lines each.
  */
  aiUsage: {
    label: 'AI usage',
    bounds: { min: 300, max: 720 },
    dock: 'right', index: 4, width: 360, visible: false, rail: true,
  },
};

export const TABLE_SURFACE = defineDetailSurface<TablePanelId>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `table-surface`, etc.
  name: 'table',
  scope: 'entity',
  ids: TABLE_PANEL_IDS,
  panes: TABLE_PANES,
  detailPane: 'detail',
});

/* ── derived, for call sites and tests that want the plain records ── */

export const TABLE_PANEL_LABELS: Record<TablePanelId, string> = TABLE_SURFACE.labels;
export const TABLE_PANEL_BOUNDS: Record<TablePanelId, PanelBounds> = TABLE_SURFACE.bounds;
export const TABLE_DEFAULT_LAYOUT: TablePanelLayout = TABLE_SURFACE.defaults;
